import React from 'react';
import { connect } from 'react-redux';
import { change, formValueSelector, reduxForm } from 'redux-form';
import { Button, Modal } from '@syneto/compass-react';
import FleetFirmwareVersions from '../../../../components/FleetFirmwareVersions';
import Form from '../../../../components/forms/form';
import { APPROVAL_FORM_NAME, EMPTY_APPROVAL_FORM_VALUES, NOT_AVAILABLE } from '../../../../constants/hardware-inventory';
import { approvedVersionOf } from '../../../../helpers/hardwareInventory';
import { useFleetFirmwareVersions } from '../../../../hooks/useFleetFirmwareVersions';
import { validateApproveFirmwareVersionForm } from './approveFirmwareVersionValidation';

const selector = formValueSelector(APPROVAL_FORM_NAME);

let ApproveFirmwareVersionModal = (props) => {
	const {
		componentType,
		approvedFirmwareVersion,
		approvedBy,
		handleSubmit,
		submitting,
		onSubmitApproval,
		onCancel,
		dispatch
	} = props;

	const fleetVersions = useFleetFirmwareVersions(componentType);
	const currentlyApproved = approvedVersionOf(componentType);

	const onSelectVersion = (version) => {
		dispatch(change(APPROVAL_FORM_NAME, 'approvedFirmwareVersion', version));
	};

	/*
	 * The picker is single-select, so what it marks as taken is the one version in the field rather
	 * than a list. Passing it as a one-member array keeps the shared component's contract unchanged
	 * - the advisory form, which selects many, is the reason it takes a list at all.
	 */
	const selectedVersions = approvedFirmwareVersion ? [approvedFirmwareVersion] : [];

	return (
		<Modal show={true} size="lg" onHide={onCancel}>
			<Modal.Header>
				<Modal.Title>Approve firmware version</Modal.Title>
			</Modal.Header>

			<Modal.Body>
				<form noValidate>
					<fieldset>
						<p className="mb-1">
							Component type <span className="text-value">
								{componentType?.displayName ?? NOT_AVAILABLE}
							</span>
						</p>

						{/*
						  * Stated rather than assumed to be remembered: this overwrites a scalar, so
						  * re-approving replaces whatever is there with no history kept, and the
						  * value being replaced is the one thing the operator needs in view.
						  */}
						<p className="text-muted">
							{currentlyApproved
								? <>Currently approved: <span className="text-value">{currentlyApproved}</span>. Approving a different version replaces it.</>
								: 'No version has been approved for this component type yet.'}
						</p>

						<FleetFirmwareVersions
							{...fleetVersions}
							selectedVersions={selectedVersions}
							onSelectVersion={onSelectVersion}
							manualEntryHint="Type a version to approve it."
							unreportedNote={`${fleetVersions.unreportedCount} component`
								+ `${fleetVersions.unreportedCount === 1 ? '' : 's'} of this type report no firmware `
								+ `version, so ${fleetVersions.unreportedCount === 1 ? 'it' : 'they'} cannot be `
								+ 'compared against the approved version.'}
						/>

						{/*
						  * Free text beside the picker, not instead of it. A version can legitimately
						  * be approved before any appliance runs it - the analytics table emits a row
						  * for a type nobody is running yet (spec §7) - so a picker built from what
						  * the fleet reports cannot be the only way in.
						  */}
						<Form.text
							label="Approved firmware version"
							name="approvedFirmwareVersion"
							id="approvedFirmwareVersion"
							required
						/>

						<p className="text-muted mb-0">
							Recorded as approved by <span className="text-value">{approvedBy}</span>.
						</p>
					</fieldset>
				</form>
			</Modal.Body>

			<Modal.Footer>
				<Button role="secondary" onClick={onCancel}>Cancel</Button>
				<Button disabled={submitting} onClick={handleSubmit(onSubmitApproval)}>Approve version</Button>
			</Modal.Footer>
		</Modal>
	);
};

/*
 * keepDirtyOnReinitialize is off for the same reason the advisory and override modals give: the
 * form is initialized from the row it was opened for, and keeping dirty values would carry a
 * version typed for one component type onto the next one opened.
 */
ApproveFirmwareVersionModal = reduxForm({
	form: APPROVAL_FORM_NAME,
	enableReinitialize: true,
	keepDirtyOnReinitialize: false,
	validate: validateApproveFirmwareVersionForm,
	shouldValidate: () => { return true; }
})(ApproveFirmwareVersionModal);

export default connect((state, componentProps) => {
	return {
		/*
		 * Prefilled with what is already approved, so re-approving a type opens on its current value
		 * rather than on a blank field that reads as "nothing is approved".
		 */
		initialValues: {
			...EMPTY_APPROVAL_FORM_VALUES,
			approvedFirmwareVersion: approvedVersionOf(componentProps.componentType) ?? ''
		},
		approvedFirmwareVersion: selector(state, 'approvedFirmwareVersion')
	};
})(ApproveFirmwareVersionModal);
